import { Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { META } from "@/content/meta";
import { PROJECTS } from "@/content/work";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Reveal } from "@/components/motion/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { FinalCTA } from "@/components/site/FinalCTA";
import { ConceptBadge } from "@/components/site/Bits";
import { BeforeAfter } from "@/components/work/BeforeAfter";

const Project = ({ project, index }) => (
  <article id={project.id} className="scroll-mt-24 border-t border-white/[0.08] py-16 lg:py-24" data-testid={`work-project-${project.id}`}>
    <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
      <Reveal className={cn("lg:col-span-7", index % 2 === 1 && "lg:order-2")}>
        <BeforeAfter project={project} />
        <p className="mt-3 text-center text-xs text-[#A1A1AA] lg:text-left">Drag the handle, or focus it and use the arrow keys, to compare.</p>
      </Reveal>
      <Reveal delay={0.1} className={cn("lg:col-span-5", index % 2 === 1 && "lg:order-1")}>
        <div className="flex flex-wrap items-center gap-3">
          <ConceptBadge testId={`concept-badge-${project.id}`} />
          <span className="font-mono text-xs text-[#A1A1AA]">
            0{index + 1} / 0{PROJECTS.length}
          </span>
        </div>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[#2DD4D4]">{project.industry}</p>
        <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl">{project.name}</h2>
        <p className="mt-4 leading-relaxed text-[#A1A1AA]">{project.summary}</p>
        <h3 className="mt-8 text-sm font-medium text-white">What the redesign focuses on</h3>
        <ul className="mt-4 space-y-3">
          {project.focus.map((f) => (
            <li key={f} className="flex gap-3 text-[15px] text-white/85">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>
        <p className="mt-8 flex items-center gap-2 text-sm text-[#A1A1AA]">
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
          Fictional business. Not a real client.
        </p>
      </Reveal>
    </div>
  </article>
);

export default function Work() {
  usePageMeta(META.work);
  return (
    <>
      <PageHero
        eyebrow="Work"
        lines={["Concept redesigns for", "North Jersey businesses."]}
        copy="We're a new studio, so instead of client logos, here's how we'd approach three kinds of local businesses. Every business below is fictional, and every design is a concept mockup. Drag the slider to compare an outdated site with our redesign."
        testId="work-hero"
      />
      <section className="pb-12" data-testid="work-projects">
        <div className="container-x">
          {PROJECTS.map((p, i) => (
            <Project key={p.id} project={p} index={i} />
          ))}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
